import React, { useState, useEffect, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Tabs,
  Tab,
  Skeleton,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import defaultLogo from "../../images/tranzcribe-logo-text-black.png";
import { useAuth, useUser, useClerk } from "@clerk/clerk-react";
import { useSelector, useDispatch } from "react-redux";
import { 
  Settings,
  Logout,
  Feedback,
  School,
  Description,
  History,
  AdminPanelSettings,
} from "@mui/icons-material";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { getErrorMessage } from "../../error-helpers/errorMessage";
import { updateUser } from "../../store/userSlice";
import { useAppSettings } from "../../contexts/AppSettingsContext";
import { useInstanceRole } from "../../hooks/useInstanceRole";

const API_URL = process.env.REACT_APP_API_URL;
const WEBSITE_ENABLED = process.env.REACT_APP_WEBSITE_ENABLED === 'true';

// Add Clerk global type definition
declare global {
  interface Window {
    Clerk: {
      openUserProfile: () => void;
    };
  }
}

const Header: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const clerk = useClerk();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.user.userData);
  const { settings, getLogoUrl } = useAppSettings();
  
  // Get admin status from instance-specific role hook
  const { isAdmin, loading: roleLoading } = useInstanceRole();
  
  // Menu state for user avatar dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Track user profile changes
  const [prevUserName, setPrevUserName] = useState<string | null>(null);

  // Determine which logo to use based on context
  const getDisplayLogo = () => {
    // If on login page, always use customized logo
    if (location.pathname.startsWith('/login')) {
      return getLogoUrl();
    }
    // If website is enabled and user is not signed in, use default Tranzcribe logo
    if (WEBSITE_ENABLED && !isSignedIn) {
      return defaultLogo;
    }
    // Otherwise use customized logo from settings
    return getLogoUrl();
  };

  // Add a state for the logo URL
  const [logoUrl, setLogoUrl] = useState(getDisplayLogo());

  // Update the logo URL when settings change or auth state changes
  useEffect(() => {
    setLogoUrl(getDisplayLogo());
  }, [getLogoUrl, settings, isSignedIn, location.pathname]);

  // Listen for user profile changes
  useEffect(() => {
    if (user) {
      const currentName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      
      // Only run this effect when the name has been loaded for the first time
      if (prevUserName === null) {
        setPrevUserName(currentName);
        return;
      }
      
      // Check if the name has changed
      if (prevUserName !== currentName && prevUserName !== null) {
        console.log('User name changed from', prevUserName, 'to', currentName);
        
        // Update the name in our backend
        updateUserNameInBackend(user.firstName || '', user.lastName || '');
        
        // Update the previous name
        setPrevUserName(currentName);
      }
    }
  }, [user?.firstName, user?.lastName]);

  // Function to update the user name in our backend
  const updateUserNameInBackend = async (firstName: string, lastName: string) => {
    try {
      // Get authentication token from Clerk
      if (!user) {
        console.error("No user found");
        return;
      }
      const token = await clerk.session?.getToken();
      
      if (!token) {
        console.error("No authentication token available");
        return;
      }
      
      // Call our backend API to update the user name
      const response = await fetch(`${API_URL}/users/update-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Clerk-User-Id': user?.id || ''
        },
        body: JSON.stringify({ firstName, lastName })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update user name: ${response.statusText}`);
      }
      
      // Update the user in Redux store
      const updatedUserData = await response.json();
      dispatch(updateUser(updatedUserData));
      
      showSuccessToast('Your profile has been updated');
    } catch (error) {
      console.error('Error updating user name:', error);
      showErrorToast(`Failed to update profile: ${getErrorMessage(error)}`);
    }
  };
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleGoToDashboard = () => {
    handleCloseMenu();
    navigate("/dashboard");
  };
  
  const handleLogout = () => {
    handleCloseMenu();
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Clerk will handle redirect to /login based on afterSignOutUrl
      await clerk.signOut();
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
      setLogoutDialogOpen(false);
    }
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileMenuNavigate = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleUserSettings = () => {
    handleCloseMenu();
    if (user) {
      // Open Clerk's user profile modal
      window.Clerk.openUserProfile();
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (userData?.firstName && userData?.lastName) {
      return `${userData.firstName.charAt(0)}${userData.lastName.charAt(0)}`.toUpperCase();
    } else if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.emailAddresses?.[0]?.emailAddress?.charAt(0).toUpperCase() || "U";
  };

  const handleLogoClick = () => {
    if (WEBSITE_ENABLED) {
      navigate("/");
    } else if (isSignedIn) {
      navigate("/dashboard");
    } else {
      navigate("/login");
    }
  };
  
  // Determine active navigation item
  const getActiveTab = () => {
    if (location.pathname === '/dashboard') return 0;
    if (location.pathname === '/history') return 1;
    if (location.pathname === '/admin') return 2;
    return false;
  };
  
  // Memoize the tabs to prevent re-renders
  const tabs = useMemo(() => {
    const tabList = [
      { label: "Dashboard", path: "/dashboard" },
      { label: "History", path: "/history" }
    ];
    
    // Only include admin tab if user is admin and role is loaded
    if (isAdmin && !roleLoading) {
      tabList.push({ label: "Admin", path: "/admin" });
    }
    
    return tabList;
  }, [isAdmin, roleLoading]);

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "white",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        padding: { xs: 1, md: 2 },
        top: 0,
        zIndex: 1000,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "80px",
          px: { xs: 1, md: 2 },
        }}
      >
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, md: 6 } }}>
          <Box 
            sx={{ 
              cursor: "pointer",
              display: "flex", 
              alignItems: "center",
              height: "100px",
              width: { xs: "140px", sm: "180px" },
              position: "relative",
              overflow: "hidden"
            }}
            onClick={handleLogoClick}
          >
            <img
              src={logoUrl}
              alt={
                location.pathname.startsWith('/login') || isSignedIn 
                  ? settings.companyName 
                  : "Tranzcribe"
              }
              style={{ 
                maxHeight: "100%", 
                maxWidth: "100%", 
                objectFit: "contain",
                position: "absolute",
                left: "0",
                height: "auto"
              }}
            />
          </Box>
          
          {/* Website Menu - only shown when WEBSITE_ENABLED and not signed in */}
          {WEBSITE_ENABLED && !isSignedIn && (
            <Box 
              sx={{ 
                display: { xs: "none", md: "flex" },
                gap: 4
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/")}
                sx={{
                  fontWeight: "500",
                  textTransform: "none",
                  color: "#333333",
                  fontSize: "1rem",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent"
                  }
                }}
              >
                Home
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/pricing")}
                sx={{
                  fontWeight: "500",
                  textTransform: "none",
                  color: "#333333",
                  fontSize: "1rem",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent"
                  }
                }}
              >
                Pricing
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/contact")}
                sx={{
                  fontWeight: "500",
                  textTransform: "none",
                  color: "#333333",
                  fontSize: "1rem",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent"
                  }
                }}
              >
                Contact
              </Button>
            </Box>
          )}
        </Box>
        
        {/* Navigation Tabs - shown when logged in (regardless of website mode) */}
        {isSignedIn && (
          <Box sx={{ ml: 4, flexGrow: 1 }}>
            {roleLoading ? (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="text" width={80} height={36} />
                <Skeleton variant="text" width={80} height={36} />
                <Skeleton variant="text" width={80} height={36} />
              </Box>
            ) : (
              <Tabs 
                value={getActiveTab()} 
                sx={{ 
                  '& .MuiTab-root': { 
                    fontSize: '15px',
                    px: { xs: 1, sm: 2 },
                    minWidth: 'auto',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: settings.primaryColor || '#1976d2',
                  },
                  '& .Mui-selected': {
                    color: `${settings.primaryColor || '#1976d2'} !important`,
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab 
                    key={tab.path}
                    label={tab.label} 
                    sx={{ textTransform: 'none' }}
                    onClick={() => navigate(tab.path)}
                  />
                ))}
              </Tabs>
            )}
          </Box>
        )}

        {/* Right-aligned Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          {/* Mobile Menu Button - Website Mode */}
          {WEBSITE_ENABLED && !isSignedIn && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/login")}
                sx={{
                  fontWeight: "600",
                  textTransform: "none",
                  color: "white",
                  fontSize: "0.9rem",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                }}
              >
                Login
              </Button>
              
              <IconButton
                onClick={handleMobileMenuOpen}
                sx={{
                  color: '#333333',
                  p: 1,
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Desktop buttons - Website Mode */}
          {WEBSITE_ENABLED && !isSignedIn && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate("/login")}
                sx={{
                  fontWeight: "500",
                  textTransform: "none",
                  color: "#333333",
                  fontSize: "1rem",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: "transparent"
                  }
                }}
              >
                Login
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/contact")}
                sx={{
                  fontWeight: "600",
                  textTransform: "none",
                  color: "white",
                  fontSize: "1rem",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                }}
              >
                Get Started
              </Button>
            </Box>
          )}

          {/* User Menu - shown when logged in */}
          {isSignedIn && (
            <Box>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  cursor: "pointer",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  background: `linear-gradient(135deg, ${settings.primaryColor || '#1976d2'}, ${settings.secondaryColor || '#115293'})`,
                  border: "2px solid white",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                onClick={handleOpenMenu}
              >
                {getUserInitials()}
              </Avatar>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                MenuListProps={{
                  "aria-labelledby": "user-button",
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    minWidth: 200,
                  },
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                {/* User Info Section */}
                <Box sx={{ px: 2, py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar 
                        sx={{ width: 40, height: 40, mr: 1.5 }}
                      >
                        {getUserInitials()}
                      </Avatar>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {user?.firstName} {user?.lastName}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Email Display */}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {user?.emailAddresses?.[0]?.emailAddress}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider />
                
                <MenuItem onClick={handleGoToDashboard}>Dashboard</MenuItem>
                
                <MenuItem onClick={() => { handleCloseMenu(); navigate('/history'); }}>
                  <History fontSize="small" sx={{ mr: 1.5 }} />
                  History
                </MenuItem>
                
                {isAdmin && (
                  <MenuItem onClick={() => { handleCloseMenu(); navigate('/admin'); }}>
                    <AdminPanelSettings fontSize="small" sx={{ mr: 1.5 }} />
                    Admin Dashboard
                  </MenuItem>
                )}
                
                <MenuItem onClick={handleUserSettings}>
                  <Settings fontSize="small" sx={{ mr: 1.5 }} />
                  Account Settings
                </MenuItem>
                

                
                <Divider />
                
                <MenuItem onClick={handleLogout} sx={{ color: '#d32f2f' }}>
                  <Logout fontSize="small" sx={{ mr: 1.5 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
      
      {/* Mobile Menu Drawer - Website Mode */}
      {WEBSITE_ENABLED && !isSignedIn && (
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: 280,
              paddingTop: 2,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
            <IconButton onClick={handleMobileMenuClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileMenuNavigate('/')}>
                <ListItemText primary="Home" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileMenuNavigate('/pricing')}>
                <ListItemText primary="Pricing" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleMobileMenuNavigate('/contact')}>
                <ListItemText primary="Contact" />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>
      )}
      
      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleCancelLogout}>
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} disabled={isLoggingOut}>Cancel</Button>
          <Button onClick={handleConfirmLogout} autoFocus disabled={isLoggingOut}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
});

export default Header; 